import React from 'react';
import { Box, Typography, Button, alpha } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useCookieConsent } from '../../contexts/CookieConsentContext';

export default function CookieConsentBanner() {
  const { t } = useTranslation('common');
  const { showBanner, acceptAll, acceptEssentials, rejectAll } = useCookieConsent();

  if (!showBanner) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: { xs: 16, sm: 24 },
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 1400,
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 4,
        p: { xs: 2.5, sm: 3 },
        width: { xs: 'calc(100vw - 32px)', sm: 560 },
        maxWidth: 560,
        boxShadow: '0 20px 50px -10px rgba(28, 25, 23, 0.15), 0 8px 20px -8px rgba(28, 25, 23, 0.1)',
        animation: 'cookieBannerSlideUp 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        '@keyframes cookieBannerSlideUp': {
          from: { opacity: 0, transform: 'translateX(-50%) translateY(30px)' },
          to: { opacity: 1, transform: 'translateX(-50%) translateY(0)' },
        },
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: 2.5,
          background: (theme) =>
            `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.15)})`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 20,
          mb: 1.5,
        }}
      >
        🍪
      </Box>

      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
        {t('cookieConsent.banner.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.55 }}>
        {t('cookieConsent.banner.body')}{' '}
        <Typography
          component="a"
          href="/privacy"
          variant="body2"
          sx={{ color: 'primary.700', textDecoration: 'underline', textUnderlineOffset: 2 }}
        >
          {t('cookieConsent.banner.privacyLink')}
        </Typography>
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1.25 }}>
        <Button
          variant="contained"
          onClick={acceptAll}
          sx={{
            flex: 1,
            bgcolor: 'primary.700',
            fontWeight: 600,
            fontSize: '0.8125rem',
            textTransform: 'none',
            borderRadius: 2,
            py: 1.25,
            boxShadow: '0 4px 12px rgba(22, 101, 52, 0.25)',
            '&:hover': { bgcolor: 'primary.600', transform: 'translateY(-1px)' },
          }}
        >
          {t('cookieConsent.banner.acceptAll')}
        </Button>
        <Button
          variant="outlined"
          onClick={acceptEssentials}
          sx={{
            flex: 1,
            borderColor: 'divider',
            color: 'text.primary',
            fontWeight: 600,
            fontSize: '0.8125rem',
            textTransform: 'none',
            borderRadius: 2,
            py: 1.25,
            '&:hover': {
              borderColor: 'primary.700',
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.05),
            },
          }}
        >
          {t('cookieConsent.banner.essentialsOnly')}
        </Button>
      </Box>

      <Box sx={{ textAlign: 'center', mt: 1 }}>
        <Button
          onClick={rejectAll}
          sx={{
            color: 'text.secondary',
            fontWeight: 500,
            fontSize: '0.75rem',
            textTransform: 'none',
            px: 1.5,
            py: 0.5,
            '&:hover': { color: 'primary.700', bgcolor: 'transparent' },
          }}
        >
          {t('cookieConsent.banner.reject')}
        </Button>
      </Box>
    </Box>
  );
}
